import { useEffect, useState } from 'react'
import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty } from 'lodash'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import FormLayout from 'features/App/components/FormLayout'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewCombo } from 'features/Snacks/components/NewCombo'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { messages as fileMessages } from 'messages/file'
import { usePushFileToS3 } from 'utils/file'
import { sanitizePrice } from 'utils/math'
import { getDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const messages = defineMessages({
  helmet: {
    id: 'combos.edit.helmet',
    defaultMessage: 'Edit Combo | Nexbus Admin'
  }
})

const EDIT_COMBO = gql`
  mutation ChangeComboItem($input: ChangeComboItemInput!) {
    changeComboItem(input: $input)
  }
`

const GET_COMBO = gql`
  query GetComboItem($comboId: ID!) {
    comboItem(input: { id: $comboId }) {
      id
      name
      photoPath
      category
      subcategory
      sellingPrice
      minSellingPrice
      components {
        quantity
        eligibleItems {
          id
          name
          netContent
          units
          container
        }
      }
    }
  }
`

const EditComboView = (): TSFixMe => {
  const navigate = useNavigate()
  const { comboId } = useParams()
  const intl = useIntl()
  const [s3Error, setS3Error] = useState()
  const login = useLogin()

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const [editCombo, { loading: mutationLoading, error: mutationError }] = useMutation(EDIT_COMBO, {
    onCompleted() {
      navigate('/snacks/combos')
    }
  })
  const pushFileToS3 = usePushFileToS3()

  const handleSubmit = async (combo: TSFixMe): Promise<TSFixMe> => {
    // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
    setS3Error()
    const previousCombo = {
      name: queryData.comboItem.name,
      category: queryData.comboItem.category,
      subcategory: queryData.comboItem.subcategory || null,
      sellingPrice: queryData.comboItem.sellingPrice,
      minSellingPrice: queryData.comboItem.minSellingPrice,
      components: queryData.comboItem.components.map((component: TSFixMe): TSFixMe => {
        return {
          quantity: parseInt(component.quantity),
          eligibleItemIDs: component.eligibleItems.map(({ id }: TSFixMe): TSFixMe => id)
        }
      })
    }

    const newCombo = {
      name: combo.name,
      category: combo.category,
      subcategory: combo.subcategory || null,
      sellingPrice: sanitizePrice(combo.sellingPrice * 100, true),
      minSellingPrice: combo.minSellingPrice ? sanitizePrice(combo.minSellingPrice * 100, true) : null,
      components: combo.components.map((component: TSFixMe): TSFixMe => {
        return {
          quantity: parseInt(component.quantity),
          eligibleItemIDs: component.eligibleItems.map(({ id }: TSFixMe): TSFixMe => id)
        }
      })
    }

    const payload = getDifference(newCombo, previousCombo)

    const currentPhoto = combo.photo || null
    if (queryData.comboItem.photoPath !== currentPhoto) {
      if (!combo.photo) {
        // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
        payload.photoPath = null
      } else {
        try {
          // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
          payload.photoPath = await pushFileToS3(combo.photo)
        } catch (ex) {
          // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
          setS3Error(intl.formatMessage(fileMessages.uploadFileError, { count: 1 }))
          return
        }
      }
    }

    if (isEmpty(payload)) {
      navigate('/snacks/combos')
    } else {
      editCombo({
        variables: {
          input: {
            id: comboId,
            item: {
              ...payload
            }
          }
        }
      })
    }
  }

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_COMBO, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      comboId
    }
  })

  useEffect((): TSFixMe => {
    refetch()
  }, [])

  if (!canWrite(FEATURES.ITEM, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.ITEM} />
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.snacks.combos.edit.error.description"
              defaultMessage="Hubo un error al cargar su combo"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="spanish.snacks.combos.edit.error.action" defaultMessage="Recargar su combo" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewCombo
        companyId={login.accessToken.companyId}
        onSubmit={handleSubmit}
        loading={mutationLoading}
        error={mutationError}
        s3Error={s3Error}
        combo={queryData.comboItem || {}}
        isEdit
      />
    )
  }

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="spanish.snacks.combos.edit.title" defaultMessage="Editar combo" />
        </Heading.H3>
      </FormLayout.Header>

      <FormLayout.Content>
        <Card.Container>{content}</Card.Container>
      </FormLayout.Content>
    </div>
  )
}

export default withLastUrl(EditComboView)
